import React from 'react';
import PropTypes from 'prop-types';

export default function Pick({
  pick_name,
  opponent_name,
  type,
  weight,
  american_odds,
  decimal_odds,
  supporting_strategies,
  total_strategies = 10,
  budget,
  showBudget = false,
  maxWeight
}) {
  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatAmericanOdds = (americanOdds, decimalOdds) => {
    // Convert to string if it's a number
    const oddsStr = String(americanOdds || '');

    // If decimal odds >= 2.0, it's positive American odds and should have a + sign
    if (decimalOdds >= 2.0) {
      // Positive American odds - ensure it has a + sign
      if (oddsStr && !oddsStr.startsWith('+') && !oddsStr.startsWith('-')) {
        return `+${oddsStr}`;
      }
      // If it already has a - sign but should be positive, convert it
      if (oddsStr && oddsStr.startsWith('-')) {
        return `+${oddsStr.substring(1)}`;
      }
      // If it already has a + sign, return as is
      if (oddsStr && oddsStr.startsWith('+')) {
        return oddsStr;
      }
    } else {
      // For negative odds (decimal < 2.0), ensure it has a - sign
      if (oddsStr && !oddsStr.startsWith('-') && !oddsStr.startsWith('+')) {
        return `-${oddsStr}`;
      }
    }
    // Return as is if already formatted correctly
    return oddsStr;
  };

  const calculateEstimatedWin = (weight, odds, budget) => {
    if (!budget) return 0;
    return budget * weight * odds;
  };

  // Bar length: the pick's stake relative to the biggest stake in the list (or its share of the budget)
  const scale = maxWeight && maxWeight > 0 ? maxWeight : 1;
  const barWidth = Math.max(0, Math.min(100, ((weight || 0) / scale) * 100));

  const isParlay = String(type).toLowerCase() === 'parlay';
  const legs = isParlay ? parlayLegs(pick_name, opponent_name) : null;
  const typeLabel = isParlay
    ? (legs ? `${legs.length}-leg parlay` : 'Parlay')
    : (String(type).toLowerCase() === 'moneyline' ? 'Moneyline' : type);

  return (
    <div className={`pick-item ${isParlay ? 'is-parlay' : 'is-moneyline'}${showBudget ? '' : ' no-budget'}`}>
      <div className="pick-fight">
        <span className="pick-type">{typeLabel}</span>
        {legs ? (
          <ul className="pick-legs">
            {legs.map((leg, i) => (
              <li key={i}>
                <span className="pick-name">{leg.fighter}</span>
                <span className="pick-opponent"><span>vs</span> {leg.opponent}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="pick-single">
            <span className="pick-name">{pick_name}</span>
            <span className="pick-opponent"><span>vs</span> {opponent_name}</span>
          </div>
        )}
      </div>
      {showBudget ? (
        <div className="pick-stat pick-stat-bet">
          <span className="pick-stat-label">Bet</span>
          <span className="pick-stat-value">${formatCurrency(budget * weight)}</span>
          <span className="pick-stat-note">{(weight * 100).toFixed(1)}% of budget</span>
          <span className="pick-bar" title={`${(weight * 100).toFixed(1)}% of budget`}>
            <span style={{ width: `${barWidth}%` }} />
          </span>
        </div>
      ) : (
        <div className="pick-stat pick-stat-bet">
          <span className="pick-stat-label">Weight</span>
          <span className="pick-stat-value">{(weight * 100).toFixed(1)}%</span>
          <span className="pick-bar" title={`${(weight * 100).toFixed(1)}% of budget`}>
            <span style={{ width: `${barWidth}%` }} />
          </span>
        </div>
      )}
      <div className="pick-stat">
        <span className="pick-stat-label">Odds</span>
        <span className="pick-stat-value">{formatAmericanOdds(american_odds, decimal_odds)}</span>
        <span className="pick-stat-note">{decimal_odds.toFixed(2)}x</span>
      </div>
      {showBudget && (
        <div className="pick-stat">
          <span className="pick-stat-label">Est. win</span>
          <span className="pick-stat-value">${formatCurrency(calculateEstimatedWin(weight, decimal_odds, budget))}</span>
        </div>
      )}
      <div className="pick-stat">
        <span className="pick-stat-label" title="Strategies that back this pick">Support</span>
        <span className="pick-stat-value">{supporting_strategies} of {total_strategies}</span>
      </div>
    </div>
  );
}

// Parlay names come joined as "A, B & C"; split them into fighter-vs-opponent legs.
// Returns null when the two sides don't split into the same number of legs.
function parlayLegs(pickName, opponentName) {
  const split = (names) => String(names || '').split(/\s*,\s*|\s+&\s+/).map(n => n.trim()).filter(Boolean);
  const fighters = split(pickName);
  const opponents = split(opponentName);
  if (fighters.length < 2 || fighters.length !== opponents.length) return null;
  return fighters.map((fighter, i) => ({ fighter, opponent: opponents[i] }));
}

// Column headings shown once above a list of picks (hidden on narrow screens, where each row labels itself)
export function PickListHeader({ showBudget = false }) {
  return (
    <div className={`pick-list-header${showBudget ? '' : ' no-budget'}`} aria-hidden="true">
      <span>Pick</span>
      <span>{showBudget ? 'Bet' : 'Weight'}</span>
      <span>Odds</span>
      {showBudget && <span>Est. win</span>}
      <span>Support</span>
    </div>
  );
}

Pick.propTypes = {
  pick_name: PropTypes.string.isRequired,
  opponent_name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['Moneyline', 'Parlay']).isRequired,
  weight: PropTypes.number.isRequired,
  american_odds: PropTypes.string.isRequired,
  decimal_odds: PropTypes.number.isRequired,
  supporting_strategies: PropTypes.number.isRequired,
  total_strategies: PropTypes.number,
  budget: PropTypes.number,
  showBudget: PropTypes.bool,
  maxWeight: PropTypes.number
};
